import { useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { createFileRoute, redirect } from '@tanstack/react-router';
import type { ColumnDef } from '@tanstack/react-table';
import { PencilSimpleIcon, TrashIcon } from '@phosphor-icons/react';
import { toast } from 'sonner';
import { z } from 'zod';
import type { PersonaDto } from '@logspot-platform/platform-types';
import {
  personasListOptions,
  lookupValuesOptions,
} from '@/hooks/query-options';
import {
  DataManagementService,
  type ProposedDataChange,
} from '@/api/data-management-service';
import { ApiError } from '@/api/api-client';
import { PageHeader } from '@/components/page-header';
import { PageContent } from '@/components/page-content';
import { DataTable } from '@/components/ui/data-table';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { SelectItem } from '@/components/ui/select';
import { FormDialog } from '@/components/ui/form-dialog';
import { ConfirmModal } from '@/components/ui/confirm-modal';
import { RowActionsMenu } from '@/components/ui/row-actions-menu';
import { useAppForm } from '@/hooks/use-app-form';

export const Route = createFileRoute(
  '/_auth/_side-layout/super-admin/data/personas',
)({
  component: SuperAdminPersonasPage,
  beforeLoad: async ({ context }) => {
    if (context.platformRole !== 'admin') {
      throw redirect({ to: '/' });
    }
  },
});

// The snake_case `proposed` payload for a persona edit (the subset of fields a
// super-admin can change). Matches the backend's persona materialization keys.
type PersonaProposed = {
  name: string;
  department: string;
  seniority: string;
  buyer_role: string;
  aliases: string[];
  cares_about: string[];
};

// Every submit path (Propose / Silent / Apply Directly) runs through these
// validators, so a silent fix or a direct write can't skip the checks a staged
// proposal enforces.
const personaFormSchema = z.object({
  name: z.string().trim().min(1, 'Name is required'),
  department: z.string().min(1, 'Department is required'),
  seniority: z.string().min(1, 'Seniority is required'),
  buyer_role: z.string().min(1, 'Buyer role is required'),
  aliases: z.string(),
  cares_about: z.string(),
});

// Splits a comma-separated string into a trimmed, non-empty list.
const splitList = (raw: string): string[] =>
  raw
    .split(',')
    .map((part) => part.trim())
    .filter(Boolean);

function PersonaEditForm({
  persona,
  onClose,
}: {
  persona: PersonaDto;
  onClose: () => void;
}) {
  const queryClient = useQueryClient();
  const [confirmDirectOpen, setConfirmDirectOpen] = useState(false);

  // The categorical Selects source their options from the DB-backed controlled
  // lookups (super-admin editable on the Lookups page) rather than
  // hardcoded consts, so the team can add/edit values without a deploy.
  const { data: departments = [] } = useQuery(
    lookupValuesOptions('persona_department'),
  );
  const { data: seniorities = [] } = useQuery(
    lookupValuesOptions('persona_seniority'),
  );
  const { data: buyerRoles = [] } = useQuery(
    lookupValuesOptions('persona_buyer_role'),
  );

  const onChangeApplied = (result: ProposedDataChange | { applied: true }) => {
    void queryClient.invalidateQueries({ queryKey: ['data-changes'] });
    void queryClient.invalidateQueries({ queryKey: ['personas'] });
    if ('applied' in result) {
      toast.success('Change applied to the persona taxonomy.');
    } else {
      toast.success(
        result.change_kind === 'silent'
          ? 'Silent fix applied.'
          : 'Change staged for review.',
      );
    }
    onClose();
  };

  const onChangeError = (error: unknown) => {
    const message =
      error instanceof ApiError ? error.message : 'Failed to submit change.';
    toast.error(message);
  };

  const mutation = useMutation({
    mutationFn: (input: {
      proposed: PersonaProposed;
      change_kind: 'material' | 'silent';
      apply_direct?: boolean;
    }) =>
      DataManagementService.propose({
        entity_type: 'persona',
        entity_key: persona.key,
        scope: 'global',
        change_kind: input.change_kind,
        proposed: input.proposed,
        apply_direct: input.apply_direct,
      }),
    onSuccess: onChangeApplied,
    onError: onChangeError,
  });

  // Which write the next submit performs. Set just before form.handleSubmit()
  // so every button runs the SAME validators (no skipping via form.state.values).
  const submitMode = useRef<{
    change_kind: 'material' | 'silent';
    apply_direct?: boolean;
  }>({ change_kind: 'material' });

  const form = useAppForm({
    defaultValues: {
      name: persona.name,
      department: persona.department,
      seniority: persona.seniority,
      buyer_role: persona.buyer_role,
      aliases: persona.aliases.join(', '),
      cares_about: persona.cares_about.join(', '),
    },
    validators: { onSubmit: personaFormSchema },
    onSubmit: async ({ value }) => {
      mutation.mutate({
        proposed: toProposed(value),
        change_kind: submitMode.current.change_kind,
        apply_direct: submitMode.current.apply_direct,
      });
    },
  });

  const toProposed = (value: {
    name: string;
    department: string;
    seniority: string;
    buyer_role: string;
    aliases: string;
    cares_about: string;
  }): PersonaProposed => ({
    name: value.name,
    department: value.department,
    seniority: value.seniority,
    buyer_role: value.buyer_role,
    aliases: splitList(value.aliases),
    cares_about: splitList(value.cares_about),
  });

  // Set the write mode, then submit through the validated path.
  const requestSubmit = (
    change_kind: 'material' | 'silent',
    apply_direct?: boolean,
  ) => {
    submitMode.current = { change_kind, apply_direct };
    void form.handleSubmit();
  };

  return (
    <FormDialog open onOpenChange={(open) => !open && onClose()} size="lg">
      <FormDialog.Header>
        <FormDialog.Title>Edit Persona</FormDialog.Title>
        <FormDialog.Description>
          {persona.key} — propose a change, apply a silent fix, or write live.
        </FormDialog.Description>
      </FormDialog.Header>
      <form
        onSubmit={(e) => {
          e.preventDefault();
          requestSubmit('material');
        }}
      >
        <FormDialog.Content>
          <form.AppForm>
            <form.AppField name="name">
              {(field) => <field.Input label="Name" required />}
            </form.AppField>
            <div className="grid gap-4 sm:grid-cols-3">
              <form.AppField name="department">
                {(field) => (
                  <field.Select label="Department">
                    {departments.map((dept) => (
                      <SelectItem key={dept.value} value={dept.value}>
                        {dept.label}
                      </SelectItem>
                    ))}
                  </field.Select>
                )}
              </form.AppField>
              <form.AppField name="seniority">
                {(field) => (
                  <field.Select label="Seniority">
                    {seniorities.map((level) => (
                      <SelectItem key={level.value} value={level.value}>
                        {level.label}
                      </SelectItem>
                    ))}
                  </field.Select>
                )}
              </form.AppField>
              <form.AppField name="buyer_role">
                {(field) => (
                  <field.Select label="Buyer Role">
                    {buyerRoles.map((role) => (
                      <SelectItem key={role.value} value={role.value}>
                        {role.label}
                      </SelectItem>
                    ))}
                  </field.Select>
                )}
              </form.AppField>
            </div>
            <form.AppField name="aliases">
              {(field) => (
                <field.Textarea
                  label="Aliases"
                  description="Comma-separated title variants for matching."
                />
              )}
            </form.AppField>
            <form.AppField name="cares_about">
              {(field) => (
                <field.Textarea
                  label="Cares About"
                  description="Comma-separated priorities and pains."
                />
              )}
            </form.AppField>
          </form.AppForm>
        </FormDialog.Content>
        <FormDialog.Footer className="flex-wrap justify-between gap-2">
          <Button
            type="button"
            variant="destructive"
            disabled={mutation.isPending}
            onClick={() => setConfirmDirectOpen(true)}
          >
            Apply Directly (Bypass Review)
          </Button>
          <div className="flex flex-wrap gap-2">
            <Button
              type="button"
              variant="outline"
              disabled={mutation.isPending}
              onClick={() => requestSubmit('silent')}
            >
              Silent Fix
            </Button>
            <Button
              type="submit"
              variant="primary"
              disabled={mutation.isPending}
            >
              Propose Change
            </Button>
          </div>
        </FormDialog.Footer>
      </form>
      <ConfirmModal
        open={confirmDirectOpen}
        variant="destructive"
        title="Apply Directly?"
        message="This bypasses staging and review, writing the change live to the persona taxonomy immediately. Intended for early testing only."
        confirmText="Apply Live Now"
        loading={mutation.isPending}
        onCancel={() => setConfirmDirectOpen(false)}
        onFinish={() => {
          setConfirmDirectOpen(false);
          requestSubmit('material', true);
        }}
      />
    </FormDialog>
  );
}

function buildColumns(
  onEdit: (persona: PersonaDto) => void,
  onDelete: (persona: PersonaDto) => void,
): ColumnDef<PersonaDto>[] {
  return [
    {
      id: 'name',
      header: 'Name',
      cell: ({ row }) => (
        <div className="flex flex-col">
          <span className="font-medium">{row.original.name}</span>
          <span className="text-muted-foreground text-xs">
            {row.original.key}
          </span>
        </div>
      ),
    },
    {
      id: 'department',
      header: 'Department',
      cell: ({ row }) => row.original.department,
    },
    {
      id: 'seniority',
      header: 'Seniority',
      cell: ({ row }) => row.original.seniority,
    },
    {
      id: 'buyer_role',
      header: 'Buyer Role',
      cell: ({ row }) => (
        <span className="text-muted-foreground text-sm">
          {row.original.buyer_role.replace(/_/g, ' ')}
        </span>
      ),
    },
    {
      id: 'source',
      header: 'Source',
      cell: ({ row }) => (
        <Badge
          variant={row.original.source === 'custom' ? 'indigo' : 'neutral'}
        >
          {row.original.source}
        </Badge>
      ),
    },
    {
      id: 'actions',
      header: '',
      cell: ({ row }) => (
        <div className="flex justify-end">
          <RowActionsMenu
            actions={[
              {
                label: 'Edit',
                icon: <PencilSimpleIcon size={16} />,
                onSelect: () => onEdit(row.original),
              },
              {
                label: 'Delete',
                icon: <TrashIcon size={16} />,
                destructive: true,
                onSelect: () => onDelete(row.original),
              },
            ]}
          />
        </div>
      ),
    },
  ];
}

// Super-admin browse + propose for the maintained persona taxonomy. Editing a
// row stages a `material` proposal (or a `silent` fix / direct write).
function PersonasAdmin() {
  const queryClient = useQueryClient();
  const { data: personas = [], isLoading } = useQuery(personasListOptions());
  const [editing, setEditing] = useState<PersonaDto | null>(null);
  const [deleting, setDeleting] = useState<PersonaDto | null>(null);

  // Deleting a persona 409s when it's still attached to a customer profile; the
  // backend message surfaces via toast (the attached-to-profiles protection).
  const deleteMutation = useMutation({
    mutationFn: (key: string) => DataManagementService.deletePersona(key),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['personas'] });
      toast.success('Persona deleted.');
      setDeleting(null);
    },
    onError: (error) => {
      const message =
        error instanceof ApiError ? error.message : 'Failed to delete persona.';
      toast.error(message);
      setDeleting(null);
    },
  });

  const columns = buildColumns(
    (persona) => setEditing(persona),
    (persona) => setDeleting(persona),
  );

  if (isLoading) {
    return <p className="text-muted-foreground text-sm">Loading personas…</p>;
  }

  return (
    <>
      <DataTable columns={columns} data={personas} manualPagination />
      {editing && (
        <PersonaEditForm
          key={editing.key}
          persona={editing}
          onClose={() => setEditing(null)}
        />
      )}
      <ConfirmModal
        open={Boolean(deleting)}
        variant="destructive"
        title="Delete Persona?"
        message={
          deleting
            ? `Delete the "${deleting.name}" persona? This cannot be undone.`
            : ''
        }
        confirmText="Delete"
        loading={deleteMutation.isPending}
        onCancel={() => setDeleting(null)}
        onFinish={() => {
          if (deleting) deleteMutation.mutate(deleting.key);
        }}
      />
    </>
  );
}

function SuperAdminPersonasPage() {
  return (
    <>
      <PageHeader
        title="Personas"
        description="Browse and propose changes to the maintained persona taxonomy."
      />
      <PageContent>
        <PersonasAdmin />
      </PageContent>
    </>
  );
}
